// src/Dashboard/pages/teacher/Events/components/EventsDataTable.jsx

import React from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaEnvelope,
  FaInfoCircle,
  FaTrash,
} from "react-icons/fa";

import {
  formatEventDate,
  getEventPhotoSrc,
  isPastEvent,
  parseEventCategories,
} from "../utils/eventsDashboardUtils";

function Switch({ checked, onChange, disabled, tone = "green", title }) {
  const tones = {
    green: "bg-emerald-500",
    blue: "bg-blue-600",
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange?.();
      }}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? tones[tone] : "bg-slate-300 dark:bg-slate-700"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function PaginationBar({ page, setPage, totalPages, totalItems, pageSize }) {
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  return (
    <div className="mb-3 flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-500 dark:text-slate-400">
        {from}–{to} sur {totalItems}
      </span>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <FaChevronLeft />
        </button>

        <span className="rounded-xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          Page {page} / {totalPages}
        </span>

        <button
          type="button"
          disabled={page === totalPages}
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

export default function EventsDataTable({
  events = [],
  page,
  setPage,
  totalPages,
  totalItems,
  pageSize,
  deletingId,
  togglingIds = {},
  onPreview,
  onEdit,
  onDelete,
  onDetails,
  onGroupMessage,
  onToggleActive,
  onToggleRegistration,
}) {
  return (
    <div className="hidden md:block">
      <PaginationBar
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
      />

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/60">
        <table className="min-w-[1220px] w-full">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <th className="px-4 py-3 text-left font-bold">Public</th>
              <th className="px-4 py-3 text-left font-bold">Inscriptions</th>
              <th className="px-4 py-3 text-left font-bold">Photo</th>
              <th className="px-4 py-3 text-left font-bold">Événement</th>
              <th className="px-4 py-3 text-left font-bold">Date</th>
              <th className="px-4 py-3 text-left font-bold">Niveau</th>
              <th className="px-4 py-3 text-left font-bold">Catégories</th>
              <th className="sticky right-0 z-30 w-44 bg-slate-50 px-4 py-3 text-right font-bold dark:bg-slate-900">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {events.map((event) => {
              const past = isPastEvent(event);
              const imgSrc = getEventPhotoSrc(event);
              const categories = parseEventCategories(event.cate_event);

              return (
                <tr
                  key={event.id}
                  className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-4 py-3">
                    <Switch
                      checked={!!event.is_active}
                      onChange={() => onToggleActive(event)}
                      disabled={!!togglingIds[`active-${event.id}`]}
                      title={
                        event.is_active
                          ? "Affiché sur le site"
                          : "Masqué du site"
                      }
                    />
                  </td>

                  <td className="px-4 py-3">
                    <Switch
                      checked={Number(event.can_register ?? 1) === 1}
                      onChange={() => onToggleRegistration(event)}
                      disabled={
                        past || !!togglingIds[`registration-${event.id}`]
                      }
                      tone="blue"
                      title={
                        past
                          ? "Événement passé"
                          : Number(event.can_register ?? 1) === 1
                          ? "Inscriptions ouvertes"
                          : "Inscriptions closes"
                      }
                    />
                  </td>

                  <td className="px-4 py-3">
                    <img
                      src={imgSrc}
                      alt={event.titre || "Événement"}
                      onClick={() => onPreview(imgSrc)}
                      onError={(imageEvent) => {
                        imageEvent.currentTarget.src = "/img/no-image.jpg";
                      }}
                      className="h-16 w-24 cursor-zoom-in rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    />
                  </td>

                  <td className="px-4 py-3">
                    <div className="max-w-[320px]">
                      <p className="font-bold text-slate-900 dark:text-white">
                        {event.titre || "—"}
                      </p>

                      <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                        {event.type_event || "Type non renseigné"}
                        {event.lieu_event ? ` · ${event.lieu_event}` : ""}
                      </p>

                      {past ? (
                        <span className="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          Passé
                        </span>
                      ) : null}
                    </div>
                  </td>

                  <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                    {formatEventDate(event)}
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      {event.level_event || "—"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    {categories.length ? (
                      <div className="flex max-w-[280px] flex-wrap gap-1.5">
                        {categories.map((category) => (
                          <span
                            key={`${event.id}-${category}`}
                            className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="sticky right-0 z-10 bg-white px-4 py-3 dark:bg-slate-900">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onDetails(event)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300"
                        title="Détails & participants"
                      >
                        <FaInfoCircle />
                      </button>

                      <button
                        type="button"
                        onClick={() => onGroupMessage(event)}
                        disabled={past}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                        title={past ? "Événement passé" : "Inviter un groupe"}
                      >
                        <FaEnvelope />
                      </button>

                      <button
                        type="button"
                        onClick={() => onEdit(event)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        title="Modifier"
                      >
                        <FaEdit />
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete(event)}
                        disabled={deletingId === event.id}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                        title="Supprimer"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
        />
      </div>
    </div>
  );
}